<template>
  <div class="d-flex flex-column gap-5">
    <div class="d-flex flex-column gap-2">
      <span class="label typography-body-sm-bold">
        {{ t('yard.drawers.createTaskDrawer.labels.assignTo') }}
      </span>

      <yard-tasks-assignee-select
        clearable
        v-model="assigneeValue"
        :disabled="disabledFields?.assignee"
        :placeholder="t('yard.drawers.createTaskDrawer.placeholders.selectAUser')" />
    </div>

    <div class="d-flex flex-column gap-2">
      <span class="label typography-body-sm-bold d-flex flex-row align-center gap-1">
        {{ t('yard.drawers.createTaskDrawer.labels.dueBy') }}
      </span>

      <div class="d-flex">
        <date-picker
          id="yard-task-due-by-date"
          data-testid="yard-task-due-by-date"
          v-model="dueByDateValue"
          :disabled="disabledFields?.dueByDate"
          :placeholder="t('yard.drawers.createTaskDrawer.placeholders.selectADate')"
          compact-display
          field-density="compact"
          outlined
          no-margins
          :display-field-label="false"
          :display-field-icon="false"
          :min-date="todayDate"></date-picker>
        <time-entry
          data-testid="yard-task-due-by-time"
          class="ml-2"
          outlined
          hide-timezone
          :disabled="disabledFields?.dueByTime"
          :timezone="selectedWarehouse?.timezone"
          :military-time-enabled="$isMilitaryTimeEnabled(selectedWarehouse)"
          v-model="dueByTimeValue"></time-entry>
      </div>
    </div>

    <div class="d-flex flex-column gap-2">
      <v-switch
        data-testid="yard-task-flag-as-urgent"
        :label="t('yard.drawers.createTaskDrawer.labels.flagAsUrgent')"
        :disabled="disabledFields?.tags"
        v-model="isUrgent"></v-switch>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { YardTaskFlags } from '@nova/core';
import useYard from '@/modules/yard/composables/useYard';

import { mapTags, updateTagsInArray } from '@satellite/helpers/yardTaskUtils';
import YardTasksAssigneeSelect from '@/modules/yard/components/Selects/YardTasksAssigneeSelect.vue';

export default defineComponent({
  name: 'YardTasksCreateTaskAssignment',
  // keeping the input event just for "consistency" with existing components; I don't think it makes sense in such context though
  emits: ['input', 'change'],
  components: {
    YardTasksAssigneeSelect
  },
  props: {
    assignee: {
      type: String,
      required: false,
      default: null
    },
    dueByDate: {
      type: String,
      required: false,
      default: ''
    },
    dueByTime: {
      type: Object,
      required: false,
      default: () => ({
        amPm: 'pm',
        time: ''
      })
    },
    tags: {
      type: Array,
      required: false,
      default: () => []
    },
    disabledFields: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const { selectedWarehouseRef } = useYard();

    const assigneeValue = ref(props.assignee ?? null);
    const dueByDateValue = ref(props.dueByDate ?? '');
    const dueByTimeValue = ref({
      amPm: 'pm',
      time: '',
      ...props.dueByTime
    });

    // Compute isUrgent from flags array
    const isUrgent = computed({
      get: () => {
        const flags = mapTags(props.tags);

        return flags.includes(YardTaskFlags.URGENT);
      },
      set: newValue => {
        const currentFlags = mapTags(props.tags);
        const newFlags = updateTagsInArray(
          currentFlags,
          newValue ? [YardTaskFlags.URGENT] : [],
          newValue ? [] : [YardTaskFlags.URGENT]
        );

        const eventData = {
          assignee: assigneeValue.value,
          dueByDate: dueByDateValue.value,
          dueByTime: dueByTimeValue.value,
          tags: newFlags
        };

        emit('input', eventData);
        emit('change', eventData);
      }
    });

    const todayDate = computed(() => {
      let today = DateTime.now();

      if (selectedWarehouseRef.value?.timezone) {
        today = today.setZone(selectedWarehouseRef.value.timezone);
      }

      return today.toFormat('yyyy-MM-dd');
    });

    watch(
      [assigneeValue, dueByDateValue, dueByTimeValue],
      ([newAssignee, newDueByDate, newDueByTime]) => {
        const eventData = {
          assignee: newAssignee,
          dueByDate: newDueByDate,
          dueByTime: newDueByTime,
          tags: mapTags(props.tags)
        };

        emit('input', eventData);
        emit('change', eventData);
      }
    );

    return {
      t,
      assigneeValue,
      dueByDateValue,
      dueByTimeValue,
      isUrgent,
      selectedWarehouse: selectedWarehouseRef,
      todayDate
    };
  }
});
</script>
